<template>
  <div class="grid">
    <div class="col-12">
      <box :title="t('Volume de Mensagens')" type="primary">
        <charts
          v-if="volumeSeries.length > 0"
          type="line"
          :series="volumeSeries"
          :options="volumeOptions"
          height="300px"
          :isLoading="isLoading"
        />
        <div v-else class="flex justify-content-center align-items-center" style="height: 300px;">
          <span class="text-color-secondary">{{ t('Sem dados para o periodo') }}</span>
        </div>
      </box>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface DailyDataPoint {
  data: string;
  mensagens_enviadas?: number;
  mensagens_recebidas?: number;
}

interface DeviceChartData {
  device_name: string;
  dados_diarios?: DailyDataPoint[];
}

interface ChartDataShape {
  por_dispositivo?: DeviceChartData[];
}

interface ChartSeries {
  name: string;
  data: Array<number | undefined>;
  color: string;
}

const props = withDefaults(defineProps<{
  chartData?: ChartDataShape | null;
  isLoading?: boolean;
}>(), {
  chartData: null,
  isLoading: false,
});

const devices = computed<DeviceChartData[]>(() => {
  if (!props.chartData || !props.chartData.por_dispositivo) return [];
  return props.chartData.por_dispositivo;
});

const categories = computed<string[]>(() => {
  if (devices.value.length === 0) return [];
  // Use the first device's daily data for categories (all devices share the same date range)
  const firstDevice = devices.value[0];
  if (!firstDevice || !firstDevice.dados_diarios) return [];
  return firstDevice.dados_diarios.map((d) => {
    const parts = d.data.split('-');
    return `${parts[2]}/${parts[1]}`;
  });
});

// Generate a color pair (sent/received) per device
const deviceColors: string[] = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];

const volumeSeries = computed<ChartSeries[]>(() => {
  if (devices.value.length === 0) return [];
  const series: ChartSeries[] = [];
  devices.value.forEach((device, i) => {
    const daily = device.dados_diarios || [];
    const colorIdx = (i * 2) % deviceColors.length;
    series.push({
      name: `${device.device_name} - ${t('Enviadas')}`,
      data: daily.map((d) => d.mensagens_enviadas),
      color: deviceColors[colorIdx]
    });
    series.push({
      name: `${device.device_name} - ${t('Recebidas')}`,
      data: daily.map((d) => d.mensagens_recebidas),
      color: deviceColors[(colorIdx + 1) % deviceColors.length]
    });
  });
  return series;
});

const volumeOptions = computed(() => ({
  chart: {
    toolbar: { show: false }
  },
  xaxis: {
    categories: categories.value
  },
  yaxis: {
    title: { text: t('Mensagens') }
  },
  stroke: {
    curve: 'smooth',
    width: 2
  },
  legend: {
    position: 'top'
  }
}));

</script>
